<template>
    <div class="mb-10 flex flex-col items-center px-5">
        <h1 class="mb-5 text-xl font-medium">
            {{ $t("onboarding.choose_integration_calendar.title") }}
        </h1>

        <div class="w-full space-y-2 text-sm">
            <div v-if="!loading" class="space-y-2">
                <div
                    v-for="calendar in integrationCalendars"
                    :key="calendar.id"
                    class="flex cursor-pointer items-center gap-5 rounded-lg border border-iron-200 p-3 hover:bg-iron-100"
                    @click="selectCalendar(calendar)"
                >
                    <div
                        class="flex h-6 w-6 items-center justify-center rounded-md border"
                        :class="[
                            isSelected(calendar) ? 'bg-z-primary-600 ' : '',
                        ]"
                    >
                        <Check
                            v-if="isSelected(calendar)"
                            stroke-width="3"
                            :size="16"
                            class="text-white"
                        />
                    </div>
                    <p class="font-medium text-iron-900">
                        {{ calendar.name }}
                    </p>
                </div>
            </div>

            <div v-if="loading" class="w-full space-y-2 text-sm">
                <Skeleton
                    class="flex h-10 cursor-pointer items-center gap-5 rounded-md border p-3 hover:bg-iron-100"
                />
                <Skeleton
                    class="flex h-10 cursor-pointer items-center gap-5 rounded-md border p-3 hover:bg-iron-100"
                />
                <Skeleton
                    class="flex h-10 cursor-pointer items-center gap-5 rounded-md border p-3 hover:bg-iron-100"
                />
                <Skeleton
                    class="flex h-10 cursor-pointer items-center gap-5 rounded-md border p-3 hover:bg-iron-100"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import route from "ziggy-js";
import { ref, defineProps, onMounted } from "vue";
import { router } from "@inertiajs/vue3";
import { Skeleton } from "@/components/ui/skeleton";
import { Check } from "lucide-vue-next";

const props = defineProps({
    integration: {
        type: Object,
        required: true,
    },
    eventType: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["calendar-selected"]);
onMounted(async () => {
    await getCalendarsForIntegration(props.integration.id);

    if (props.eventType.integration_id) {
        const settings = JSON.parse(props.eventType.settings);
        const integrationCalendarId = settings.id ?? settings.calendar_url;
        selectedCalendar.value = integrationCalendars.value.find(
            (calendar) => calendar.id === integrationCalendarId,
        );
        if (!selectedCalendar.value) {
            selectedCalendar.value = integrationCalendars.value.find(
                (calendar) => integrationCalendarId.endsWith(calendar.id),
            );
        }
    }
});

const integrationCalendars = ref<any[]>([]);
const selectedCalendar = ref(undefined as any);
const loading = ref(false);

async function getCalendarsForIntegration(integrationId: number) {
    loading.value = true;

    try {
        const res = await axios.get(
            route("app.integrations.calendars.index", {
                integration: integrationId,
            }),
        );

        integrationCalendars.value = res.data.data;

        selectedCalendar.value = integrationCalendars.value[0].id;
    } catch (err) {
        console.error(err);
    }

    loading.value = false;
}

function isSelected(calendar: any) {
    return (
        selectedCalendar.value &&
        (selectedCalendar.value.id === calendar.id ||
            calendar.id.endsWith(selectedCalendar.value.id))
    );
}

function selectCalendar(calendar: any) {
    if (selectedCalendar.value && selectedCalendar.value.id === calendar.id) {
        selectedCalendar.value = undefined;

        router.post(
            route("app.event-types.integration.assign", {
                eventType: props.eventType.id,
            }),
            {},
            {
                onSuccess: () => {
                    emit("calendar-selected");
                },
                preserveScroll: true,
                preserveState: true,
            },
        );
        return;
    }

    selectedCalendar.value = calendar;

    router.post(
        route("app.event-types.integration.assign", {
            eventType: props.eventType.id,
        }),
        {
            integration_id: props.integration.id,
            integration_calendar_id: calendar.id,
            integration_calendar_name: calendar.name,
            integration_calendar_default: calendar.default,
        },
        {
            preserveScroll: true,
            preserveState: true,
        },
    );
}
</script>
